import { EyeIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { Button } from '@heroui/react';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';
import { NoData } from '@/app/components/no-data.tsx';
import { OnboardImportStatusIndicator } from '@/app/components/onboarding/onboard-import-status-indicator.tsx';
import OnboardJsonLineItemDialog from '@/app/components/onboarding/onboard-json-line-item-dialog.tsx';
import { Paginator, usePagination } from '@/app/components/paginator.tsx';
import { formatDate } from '@/app/helpers/date.helpers.ts';
import { Field } from '@/primitives/fieldset.tsx';
import { Input, InputGroup } from '@/primitives/input.tsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useSort,
} from '@/primitives/table.tsx';
import { TextLink } from '@/primitives/text.tsx';
import { useFilter } from '../../hooks/use-filter.ts';
import type { OnboardImportAvailabilitySlotLineItemModel } from '../../types/onboard-import.type.ts';

const AvailabilitySlotImportTable = ({
  lineItems,
}: {
  lineItems: OnboardImportAvailabilitySlotLineItemModel[];
}) => {
  const [lineOpen, setLineOpen] = useState(false);
  const [lineItem, setLineItem] = useState(null);

  const memoizedSortingDataAccessor = useCallback(
    (value: OnboardImportAvailabilitySlotLineItemModel, col: string) => {
      switch (col) {
        case 'status':
          return _(value.onboardImportStatuses)
            .map(x => x.onboardImportStatusType.id)
            .max();
        case 'date':
          return DateTime.fromISO(value.availabilitySlot.start).isValid
            ? DateTime.fromISO(value.availabilitySlot.start)
            : DateTime.fromISO(value.jsonLineItem.date);
        case 'time':
          return formatDate(
            value.availabilitySlot.start,
            DateTime.TIME_SIMPLE,
            formatDate(value.jsonLineItem.startTime, DateTime.TIME_SIMPLE),
          );
        default:
          return _.get(value, col);
      }
    },
    [],
  );

  const { sort, setSort, sortData } = useSort({
    data: lineItems,
    memoizedSortingDataAccessor,
  });

  const { filter, setFilter, filteredData } = useFilter({ data: sortData });
  const paginator = usePagination({ data: filteredData });
  const { pagedData } = paginator;

  return (
    <>
      <Field className='pb-5 lg:w-1/6'>
        <InputGroup>
          <Input
            type='text'
            onInput={(event: any) => setFilter(event.target.value)}
            value={filter}
            placeholder='Filter text'
          />
          <MagnifyingGlassIcon />
        </InputGroup>
      </Field>
      <Table currentSort={sort} setCurrentSort={setSort}>
        <TableHead>
          <TableRow>
            <TableHeader sortColumn='status'>Status</TableHeader>
            <TableHeader sortColumn='rowNumber'>Row #</TableHeader>
            <TableHeader sortColumn='date'>Date</TableHeader>
            <TableHeader sortColumn='time'>Time</TableHeader>
            <TableHeader>Notes</TableHeader>
            <TableHeader></TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {pagedData.length ? (
            pagedData.map(value => (
              <TableRow key={value.id}>
                <TableCell>
                  <OnboardImportStatusIndicator
                    status={
                      _(value.onboardImportStatuses)
                        .orderBy(x => x.onboardImportStatusType.id, 'desc')
                        .head()?.onboardImportStatusType
                    }
                  />
                </TableCell>
                <TableCell>{value.rowNumber}</TableCell>
                <TableCell>
                  {DateTime.fromISO(value.availabilitySlot?.start).isValid ? (
                    <TextLink href={`/availability/details/${value.availabilitySlot.key}`}>
                      {formatDate(value.availabilitySlot.start, DateTime.DATE_SHORT)}
                    </TextLink>
                  ) : (
                    value.jsonLineItem?.date
                  )}
                </TableCell>
                <TableCell>
                  {formatDate(
                    value.availabilitySlot?.start,
                    DateTime.TIME_SIMPLE,
                    value.jsonLineItem?.startTime,
                  )}{' '}
                  -&nbsp;
                  {formatDate(
                    value.availabilitySlot?.end,
                    DateTime.TIME_SIMPLE,
                    value.jsonLineItem?.endTime,
                  )}
                </TableCell>
                <TableCell>{value.onboardImportStatuses.map(x => x.notes).join(' ')}</TableCell>
                <TableCell>
                  <Button
                    variant='light'
                    onClick={() => {
                      setLineItem(value.jsonLineItem);
                      setLineOpen(true);
                    }}
                    title='View line item'
                    isIconOnly
                  >
                    <EyeIcon />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={9}>
                <NoData />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Paginator {...paginator} />
      <OnboardJsonLineItemDialog
        isOpen={lineOpen}
        setOpen={setLineOpen}
        jsonLineItem={lineItem}
        title={'Availability Slot Line Item'}
      />
    </>
  );
};

export default AvailabilitySlotImportTable;
